import type { ChangeEvent, FC } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SelectChangeEvent } from '@mui/material';
import {
  Box,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useAtom } from 'jotai';

import { isPlanActionOrgUnit } from 'src/types/orgUnit';
import type { Progress } from 'src/types/planAction';
import { usePlanActionContext } from 'src/components/planAction/PlanActionContext';
import {
  actionDeadlineAtom,
  actionNoteAtom,
  actionOwnerAtom,
  actionProgressAtom,
  actionTitleAtom,
} from 'src/components/planAction/PlanActionDetailPage/components/Action/atoms';
import { useTouchActionPlan } from 'src/components/planAction/PlanActionDetailPage/components/Action/hooks';

import TargetStatementSelector from './components/TargetStatementSelector';

import { styles } from './index.styles';

export const ACTION_PLAN_ACTION_DEADLINE_TEST_ID =
  'action-plan-action-deadline';

interface BasicDataProps {
  actionId: number;
  editable?: boolean;
  errors: {
    title: string;
    notes: string;
  };
}

const BasicData: FC<BasicDataProps> = ({ actionId, editable, errors }) => {
  const { t } = useTranslation('planAction');
  const { selectedOrgUnit } = usePlanActionContext();
  const selectedPlanActionOrgUnit = isPlanActionOrgUnit(selectedOrgUnit)
    ? selectedOrgUnit
    : { plan_action_due_date: null };
  const [titleAtom, setTitleAtom] = useAtom(actionTitleAtom(actionId));
  const [noteAtom, setNoteAtom] = useAtom(actionNoteAtom(actionId));
  const [ownerAtom, setOwnerAtom] = useAtom(actionOwnerAtom(actionId));
  const [deadlineAtom, setDeadlineAtom] = useAtom(actionDeadlineAtom(actionId));
  const [progressAtom, setProgressAtom] = useAtom(actionProgressAtom(actionId));
  const [title, setTitle] = useState(titleAtom);
  const [note, setNote] = useState(noteAtom);
  const [owner, setOwner] = useState(ownerAtom);
  const [deadline, setDeadline] = useState(
    deadlineAtom || selectedPlanActionOrgUnit.plan_action_due_date,
  );
  const [progress, setProgress] = useState(String(progressAtom ?? 0));
  const { touchActionPlan } = useTouchActionPlan();

  const handleTitleChange = useCallback(
    (event: ChangeEvent<any>) => {
      setTitle(event.target.value);
      setTitleAtom(event.target.value);
      touchActionPlan();
    },
    [setTitleAtom, touchActionPlan],
  );

  const handleNoteChange = useCallback(
    (event: ChangeEvent<any>) => {
      setNote(event.target.value);
      setNoteAtom(event.target.value);
      touchActionPlan();
    },
    [setNoteAtom, touchActionPlan],
  );

  const handleOwnerChange = useCallback(
    (event: ChangeEvent<any>) => {
      setOwner(event.target.value);
      setOwnerAtom(event.target.value);
      touchActionPlan();
    },
    [touchActionPlan, setOwnerAtom],
  );

  const handleDeadlineChange = useCallback(
    (date: Date | null) => {
      setDeadline(
        date
          ? `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
          : null,
      );
      setDeadlineAtom(
        date
          ? `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
          : undefined,
      );
      touchActionPlan();
    },
    [touchActionPlan, setDeadlineAtom],
  );

  const handleProgressChange = useCallback(
    (event: SelectChangeEvent) => {
      setProgress(String(event.target.value));
      setProgressAtom(Number(event.target.value) as Progress);
      touchActionPlan();
    },
    [touchActionPlan, setProgressAtom],
  );

  return (
    <Stack direction="column" spacing={2}>
      <Box>
        <Typography variant="h6">
          {`${t('ActionPlan.ActionTitle')}*`}
        </Typography>

        <OutlinedInput
          size="small"
          fullWidth
          placeholder={`${t('ActionPlan.ActionTitlePlaceholder')} (${t(
            'planAction:ActionPlan.PersonalInformationWarning',
          )})`}
          name="title"
          sx={styles.inputField}
          id={`plan-action-title-input-${actionId}`}
          value={title}
          onChange={handleTitleChange}
          readOnly={!editable}
          error={!!errors && !!errors.title}
        />
      </Box>
      <Stack direction="row" spacing={1}>
        <Box
          sx={{
            flexGrow: 10,
          }}
        >
          <Typography variant="h6">{t('ActionPlan.ActionOwner')}</Typography>
          <OutlinedInput
            size="small"
            fullWidth
            sx={styles.inputField}
            placeholder={t('ActionPlan.ActionOwnerPlaceholder')}
            name="owner"
            id={`plan-action-owner-input-${actionId}`}
            value={owner}
            onChange={handleOwnerChange}
            readOnly={!editable}
            error={!!errors && !!errors.title}
          />
        </Box>

        <Box
          data-test-id={ACTION_PLAN_ACTION_DEADLINE_TEST_ID}
          sx={{
            width: '40%',
          }}
        >
          <Typography variant="h6">
            {t('ActionPlan.ImplementationDeadline')}
          </Typography>
          <DatePicker
            defaultValue={deadline ? new Date(deadline) : null}
            onChange={handleDeadlineChange}
            maxDate={
              selectedPlanActionOrgUnit.plan_action_due_date
                ? new Date(selectedPlanActionOrgUnit.plan_action_due_date)
                : undefined
            }
            views={['year', 'month', 'day']}
            format="dd.MM.yyyy"
          />
        </Box>
      </Stack>
      <Box
        sx={{
          width: '60%',
        }}
      >
        <Typography variant="h6">
          {t('ActionPlan.ImplementationStatus')}
        </Typography>
        <Select
          fullWidth
          size="small"
          name="progress"
          onChange={handleProgressChange}
          value={progress}
          disabled={!editable}
          sx={styles.inputField}
        >
          <MenuItem value="0">0%</MenuItem>
          <MenuItem value="25">25%</MenuItem>
          <MenuItem value="50">50%</MenuItem>
          <MenuItem value="75">75%</MenuItem>
          <MenuItem value="100">100%</MenuItem>
          <MenuItem value="-1">
            {t('ActionPlan.ActionStatusDiscontinued')}
          </MenuItem>
        </Select>
      </Box>
      <TargetStatementSelector actionId={actionId} editable={editable} />
      <Typography variant="h6">
        {`${t('planAction:ActionPlan.ActionNotePlaceholder')}*`}
      </Typography>
      <TextField
        placeholder={`(${t(
          'planAction:ActionPlan.PersonalInformationWarning',
        )})`}
        name="note"
        id={`plan-action-note-input-${actionId}`}
        value={note}
        onChange={handleNoteChange}
        InputProps={{ readOnly: !editable }}
        type="textarea"
        error={!!errors && !!errors.notes}
        multiline
        rows={12}
      />
    </Stack>
  );
};

export default BasicData;
